import { generateUUID } from '../utils/uuid';
/**
 * CompactView — mini RAAPID summary rendered in the EHR shell's right sidebar.
 * Loaded at /compact?mode=mock&theme=X&patientId=Y (same-origin iframe).
 *
 * Shows: RAF scores, gap counts, top suggestions with inline accept/reject.
 * postMessages parent shell to open the full panel tab.
 */
import { useState } from 'react';
import { useSession } from '../context/SessionContext';
import { useCodingSuggestions } from '../hooks/useCodingSuggestions';
import type { GapType, CodingSuggestion, SuggestionStatus, DocumentType, FilterState, SortField } from '../types';
import { isInScope } from '../utils/specialtyMapping';
import { PROVIDER_SPECIALTIES } from '../utils/specialtyMapping';

function openFullPanel() {
  window.parent?.postMessage({ raapid: 'openFull' }, window.location.origin);
}

const GAP_TYPES_COMPACT: { value: GapType; label: string }[] = [
  { value: 'suspect_new', label: 'Suspecting Code Gap' },
  { value: 'opportunity', label: 'Recaptured Opportunity' },
  { value: 'recapture', label: 'Recaptured Existing' },
  { value: 'recapture_stale', label: 'Suspecting Emerging' },
  { value: 'over_claimed', label: 'Recaptured Risk' },
];

const STATUSES_COMPACT: { value: SuggestionStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

const DOC_TYPES_COMPACT: { value: DocumentType; label: string }[] = [
  { value: 'progress_note', label: 'Progress Note' },
  { value: 'lab_result', label: 'Lab Result' },
  { value: 'consult', label: 'Consult' },
  { value: 'medication_list', label: 'Med List' },
  { value: 'procedure_report', label: 'Procedure' },
  { value: 'vital_signs', label: 'Vitals' },
  { value: 'imaging', label: 'Imaging' },
  { value: 'discharge_summary', label: 'Discharge' },
  { value: 'other', label: 'Other' },
];

export default function CompactView() {
  const { launch } = useSession();
  const { data, isLoading, filters, setFilters, filteredSuggestions, acceptSuggestion, rejectSuggestion, resetSuggestionStatus, addSuggestion, editSuggestion, actionLoading, attestation, attestSession, conditionGroups } = useCodingSuggestions();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'gapType' | 'condition'>('gapType');
  const [attestWarning, setAttestWarning] = useState<{ message: string; allowAttest: boolean } | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const accent = launch.theme === 'ecw' ? '#00897B' : '#0079C1';
  const accentLight = launch.theme === 'ecw' ? '#E8F5F3' : '#E8F4FC';

  if (isLoading || !data) {
    return (
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {[80, 60, 100, 60].map((w, i) => (
          <div key={i} style={{ height: '12px', width: `${w}%`, background: '#E5E7EB', borderRadius: '4px', animation: 'pulse 1.5s infinite' }} />
        ))}
      </div>
    );
  }

  // Progress bar uses filtered suggestions (matches main panel's GapSummaryBar)
  const filteredTotal = filteredSuggestions.length;
  const filteredReviewed = filteredSuggestions.filter((s) => s.status !== 'pending').length;
  const pct = filteredTotal > 0 ? Math.round((filteredReviewed / filteredTotal) * 100) : 0;
  // List rendering uses filtered suggestions
  const pending = filteredSuggestions.filter((s) => s.status === 'pending');
  const reviewed = filteredSuggestions.filter((s) => s.status !== 'pending');
  const accepted = reviewed.filter((s) => s.status === 'accepted').length;
  const rejected = reviewed.filter((s) => s.status === 'rejected').length;
  const isFiltered = filteredSuggestions.length !== data.suggestions.length;
  const activeFilterCount = [
    filters.gapType !== 'all',
    filters.status !== 'all',
    filters.specialty !== 'all',
    filters.mySpecialtyOnly,
    filters.hccOnly,
    !!filters.searchQuery,
  ].filter(Boolean).length;

  const handleFilterChange = (updated: Partial<FilterState>) => setFilters((prev) => ({ ...prev, ...updated }));

  const allReviewed = filteredTotal > 0 && filteredSuggestions.filter(s => s.status !== 'pending').length === filteredTotal;

  const handleAttest = () => {
    const allSuggestions = data.suggestions;
    const inScopePending = allSuggestions.filter((s) => s.status === 'pending' && isInScope(s.hccCategory, launch.providerSpecialty)).length;
    const outOfScopePending = allSuggestions.filter((s) => s.status === 'pending' && !isInScope(s.hccCategory, launch.providerSpecialty)).length;
    const isSpecialist = launch.providerSpecialty && launch.providerSpecialty !== 'PCP';
    if (inScopePending > 0) {
      const scope = isSpecialist ? ` ${launch.providerSpecialty}` : '';
      setAttestWarning({ message: `Review ${inScopePending} remaining${scope} suggestion${inScopePending > 1 ? 's' : ''} before attesting.`, allowAttest: false });
    } else if (outOfScopePending > 0 && isSpecialist) {
      setAttestWarning({ message: `${launch.providerSpecialty} review complete. ${outOfScopePending} code${outOfScopePending > 1 ? 's' : ''} remain for other specialties.`, allowAttest: true });
    } else if (outOfScopePending > 0) {
      setAttestWarning({ message: `${outOfScopePending} suggestion${outOfScopePending > 1 ? 's' : ''} remaining. Continue to attest?`, allowAttest: true });
    } else {
      attestSession(launch.providerName ?? 'Provider');
    }
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflowX: 'hidden',
      background: '#F9FAFB',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: '13px',
      color: '#1F2937',
    }}>

      {/* Patient header */}
      <div style={{ background: '#1E293B', padding: '8px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: 'white', flexShrink: 0 }}>
            {data.patient.firstName[0]}{data.patient.lastName[0]}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {data.patient.lastName}, {data.patient.firstName}
            </div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)' }}>MRN: {data.patient.mrn}</div>
          </div>
          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', textAlign: 'right', flexShrink: 0 }}>
            <div>{data.encounter.encounterType}</div>
            <div>{new Date(data.encounter.encounterDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            `Age ${data.patient.age}`,
            data.patient.gender,
            data.patient.insurancePlan,
            `PCP: ${data.patient.pcp}`,
          ].map((item) => (
            <span key={item} style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)' }}>{item}</span>
          ))}
          {data.patient.riskAdjustmentEligible && (
            <span style={{ fontSize: '9px', fontWeight: 700, background: '#FBBF24', color: '#78350F', borderRadius: '3px', padding: '1px 4px' }}>MA ELIGIBLE</span>
          )}
        </div>
        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
          Attending: {data.encounter.attendingPhysician}{launch.providerSpecialty ? ` · ${launch.providerSpecialty}` : ''}
        </div>
      </div>

      {/* Sub-header: title + view toggle */}
      <div style={{ background: accentLight, borderBottom: `1px solid ${accent}20`, padding: '6px 12px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '2px', background: '#E5E7EB', borderRadius: '5px', padding: '2px' }}>
          <button onClick={() => setViewMode('gapType')} style={{ padding: '2px 8px', fontSize: '10px', fontWeight: 700, borderRadius: '3px', border: 'none', cursor: 'pointer', background: viewMode === 'gapType' ? 'white' : 'transparent', color: viewMode === 'gapType' ? '#374151' : '#9CA3AF', boxShadow: viewMode === 'gapType' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none' }}>Gap Type</button>
          <button onClick={() => setViewMode('condition')} style={{ padding: '2px 8px', fontSize: '10px', fontWeight: 700, borderRadius: '3px', border: 'none', cursor: 'pointer', background: viewMode === 'condition' ? 'white' : 'transparent', color: viewMode === 'condition' ? '#374151' : '#9CA3AF', boxShadow: viewMode === 'condition' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none' }}>By Condition</button>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

      {/* Collapsible filter/sort bar */}
      <div style={{ borderBottom: '1px solid #F3F4F6', background: '#FAFAFA' }}>
        {/* Toggle row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px' }}>
          <button
            onClick={() => setShowFilters((v) => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 600, border: '1px solid #E5E7EB', borderRadius: '5px', background: showFilters ? '#F3F4F6' : 'white', color: '#374151', cursor: 'pointer' }}
          >
            <svg width="11" height="11" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6 10a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zM9 15a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" /></svg>
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
          <div style={{ flex: 1 }} />
          {/* Sort inline */}
          <CompactSelect value={filters.sortBy} onChange={(v) => handleFilterChange({ sortBy: v as SortField })} options={[{ value: 'clinicalPriority', label: 'Clinical Priority' }, { value: 'rafImpact', label: 'RAF Impact' }, { value: 'gapType', label: 'Gap Type' }, { value: 'icdCode', label: 'ICD Code' }]} />
          <button onClick={() => handleFilterChange({ sortDir: filters.sortDir === 'asc' ? 'desc' : 'asc' })} style={{ background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: '3px', padding: '2px 5px', cursor: 'pointer', fontSize: '11px', color: '#6B7280', lineHeight: 1 }} title={filters.sortDir === 'asc' ? 'Sort descending' : 'Sort ascending'}>{filters.sortDir === 'asc' ? '↑' : '↓'}</button>
        </div>
        {/* Expanded filter panel */}
        {showFilters && (
          <div style={{ padding: '6px 12px 8px', borderTop: '1px solid #F3F4F6' }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '6px' }}>
              <CompactSelect value={filters.gapType} onChange={(v) => handleFilterChange({ gapType: v as FilterState['gapType'] })} options={[{ value: 'all', label: 'All Types' }, ...GAP_TYPES_COMPACT.map(g => ({ value: g.value, label: g.label }))]} />
              <CompactSelect value={filters.status} onChange={(v) => handleFilterChange({ status: v as FilterState['status'] })} options={[{ value: 'all', label: 'All Status' }, ...STATUSES_COMPACT.map(s => ({ value: s.value, label: s.label }))]} />
              <CompactSelect value={filters.specialty} onChange={(v) => handleFilterChange({ specialty: v as FilterState['specialty'] })} options={[{ value: 'all', label: 'All Specialties' }, ...PROVIDER_SPECIALTIES.map(s => ({ value: s, label: s }))]} />
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              {launch.providerSpecialty && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={filters.mySpecialtyOnly} onChange={(e) => handleFilterChange({ mySpecialtyOnly: e.target.checked })} style={{ width: '10px', height: '10px' }} />
                  <span style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600 }}>My Specialty</span>
                </label>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer' }}>
                <input type="checkbox" checked={filters.hccOnly} onChange={(e) => handleFilterChange({ hccOnly: e.target.checked })} style={{ width: '10px', height: '10px' }} />
                <span style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600 }}>HCC Only</span>
              </label>
            </div>
            {isFiltered && (
              <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px', textAlign: 'right' }}>
                Showing {filteredSuggestions.length} of {data.suggestions.length}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gap chips — counts from filtered suggestions */}
      <div style={{ padding: '8px 12px', display: 'flex', gap: '6px', flexWrap: 'wrap', borderBottom: '1px solid #F3F4F6' }}>
        {filteredSuggestions.filter(s => s.gapType === 'suspect_new').length > 0 && (
          <Chip count={filteredSuggestions.filter(s => s.gapType === 'suspect_new').length} label="Suspecting Code Gap" bg="#EFF6FF" color="#1D4ED8" border="#BFDBFE" />
        )}
        {filteredSuggestions.filter(s => s.gapType === 'opportunity').length > 0 && (
          <Chip count={filteredSuggestions.filter(s => s.gapType === 'opportunity').length} label="Recaptured Opportunity" bg="#F0FDF4" color="#15803D" border="#BBF7D0" />
        )}
        {filteredSuggestions.filter(s => s.gapType === 'recapture').length > 0 && (
          <Chip count={filteredSuggestions.filter(s => s.gapType === 'recapture').length} label="Recaptured Existing" bg="#F5F3FF" color="#6D28D9" border="#DDD6FE" />
        )}
        {filteredSuggestions.filter(s => s.gapType === 'recapture_stale').length > 0 && (
          <Chip count={filteredSuggestions.filter(s => s.gapType === 'recapture_stale').length} label="Suspecting Emerging" bg="#FFFBEB" color="#B45309" border="#FDE68A" />
        )}
        {filteredSuggestions.filter(s => s.gapType === 'over_claimed').length > 0 && (
          <Chip count={filteredSuggestions.filter(s => s.gapType === 'over_claimed').length} label="Recaptured Risk" bg="#FEF2F2" color="#B91C1C" border="#FECACA" />
        )}
        {accepted > 0 && (
          <Chip count={accepted} label="Accepted" bg="#ECFDF5" color="#059669" border="#A7F3D0" />
        )}
        {rejected > 0 && (
          <Chip count={rejected} label="Rejected" bg="#F3F4F6" color="#6B7280" border="#E5E7EB" />
        )}
      </div>

      {/* ── Condition View (compact) ─────────────────────────────── */}
      {viewMode === 'condition' && (
        <div style={{ padding: '4px 0' }}>
          {conditionGroups.length === 0 ? (
            <div style={{ padding: '20px 12px', textAlign: 'center', color: '#9CA3AF', fontSize: '11px' }}>
              No conditions to display
            </div>
          ) : conditionGroups.map((group) => {
            const groupKey = group.hccCategory ?? group.suggestions[0]?.icdCode ?? group.problemListItems[0]?.icdCode ?? 'other';
            const label = group.hccDescription ?? group.suggestions[0]?.icdDescription ?? group.problemListItems[0]?.icdDescription ?? 'Other';
            const hasOverClaimed = group.suggestions.some(s => s.gapType === 'over_claimed');
            return (
              <div key={groupKey} style={{ borderBottom: '2px solid #E5E7EB' }}>
                {/* Group header */}
                <div style={{ padding: '6px 12px', background: hasOverClaimed ? '#FEF2F2' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: hasOverClaimed ? '#991B1B' : '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                    {group.hccCategory && <span style={{ fontSize: '10px', fontFamily: 'monospace', color: '#6B7280', background: '#E5E7EB', padding: '1px 4px', borderRadius: '3px', flexShrink: 0 }}>{group.hccCategory}</span>}
                    {hasOverClaimed && <span style={{ fontSize: '10px', fontWeight: 700, color: '#B91C1C', flexShrink: 0 }}>⚠</span>}
                  </div>
                  <span style={{ fontSize: '10px', color: '#9CA3AF', flexShrink: 0 }}>{group.problemListItems.length + group.suggestions.length}</span>
                </div>
                {/* Problem list items */}
                {group.problemListItems.map((p) => (
                  <div key={p.icdCode} style={{ padding: '5px 12px', display: 'flex', alignItems: 'center', gap: '6px', borderBottom: '1px solid #F3F4F6', background: 'white' }}>
                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#059669', background: '#ECFDF5', border: '1px solid #A7F3D0', padding: '1px 4px', borderRadius: '8px', flexShrink: 0 }}>✓ On List</span>
                    <span style={{ fontSize: '10px', fontFamily: 'monospace', color: '#6B7280', flexShrink: 0 }}>{p.icdCode}</span>
                    <span style={{ fontSize: '11px', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.icdDescription}</span>
                  </div>
                ))}
                {/* Suggestions */}
                {group.suggestions.map((s) => {
                  const loading = actionLoading[s.id] ?? false;
                  const gapColor = s.gapType === 'suspect_new' ? '#1D4ED8' : s.gapType === 'opportunity' ? '#15803D' : s.gapType === 'over_claimed' ? '#B91C1C' : s.gapType === 'recapture_stale' ? '#B45309' : '#6D28D9';
                  const gapLabel = s.gapType === 'suspect_new' ? 'Suspecting Code Gap' : s.gapType === 'opportunity' ? 'Recaptured Opportunity' : s.gapType === 'over_claimed' ? 'Recaptured Risk' : s.gapType === 'recapture_stale' ? 'Suspecting Emerging' : 'Recaptured Existing';
                  const isActioned = s.status !== 'pending';
                  const isNonHCC = !s.hccCategory;
                  return (
                    <div key={s.id} style={{ padding: '6px 12px', borderBottom: '1px solid #F3F4F6', background: isActioned ? '#FAFAFA' : 'white', opacity: isActioned ? 0.75 : isNonHCC && s.status === 'pending' ? 0.7 : 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: gapColor, background: `${gapColor}15`, padding: '1px 4px', borderRadius: '6px' }}>{gapLabel}</span>
                            {s.status === 'accepted' && <span style={{ fontSize: '10px', fontWeight: 700, color: '#059669', background: '#ECFDF5', padding: '1px 4px', borderRadius: '4px' }}>NEW</span>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap', marginBottom: '2px' }}>
                            <span style={{ fontSize: '15px', fontWeight: 700, fontFamily: 'monospace', color: isActioned ? '#6B7280' : '#111827', letterSpacing: '0.02em', flexShrink: 0 }}>{s.icdCode}</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: isActioned ? '#6B7280' : '#1F2937', lineHeight: 1.3 }}>{s.icdDescription}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
                          {s.status === 'pending' ? (
                            <>
                              <button onClick={() => acceptSuggestion(s.id)} disabled={loading} style={{ background: accent, color: 'white', border: 'none', borderRadius: '3px', padding: '3px 6px', fontSize: '11px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>✓</button>
                              <button onClick={() => setEditingId(s.id)} disabled={loading} title="Edit before accepting" style={{ background: '#F3F4F6', color: '#9CA3AF', border: '1px solid #E5E7EB', borderRadius: '3px', padding: '3px 6px', fontSize: '11px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>✎</button>
                              <button onClick={() => rejectSuggestion(s.id, 'Rejected')} disabled={loading} style={{ background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: '3px', padding: '3px 6px', fontSize: '11px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>✕</button>
                            </>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <span style={{ fontSize: '11px', fontWeight: 700, color: s.status === 'accepted' ? '#059669' : '#6B7280' }}>{s.status === 'accepted' ? '✓' : '✕'}</span>
                              <button onClick={() => resetSuggestionStatus(s.id)} style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: '3px', padding: '1px 4px', cursor: 'pointer', fontSize: '11px', color: '#9CA3AF' }}>↩</button>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Expand toggle — condition view */}
                      {(s.clinicalReasoning || s.evidence?.length > 0) && (
                        <button
                          onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', padding: '2px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: '10px', fontWeight: 600 }}
                        >
                          <span style={{ fontSize: '9px', transform: expandedId === s.id ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform 0.15s' }}>▶</span>
                          Justification{s.evidence?.length > 0 ? ` · ${s.evidence.length} source${s.evidence.length !== 1 ? 's' : ''}` : ''}
                        </button>
                      )}
                      {expandedId === s.id && <CompactEvidencePanel suggestion={s} accent={accent} />}
                      {editingId === s.id && (
                        <CompactEditForm
                          suggestion={s}
                          accent={accent}
                          onSave={(updates) => { editSuggestion(s.id, updates); setEditingId(null); }}
                          onCancel={() => setEditingId(null)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* ADD-NEW-CODE: DO NOT DELETE — commented out for point-of-care demo, re-enable when manual code entry is needed.
      <div style={{ padding: '8px 12px 0' }}>
        {showAddForm ? (
          <CompactAddForm
            accent={accent}
            onAdd={(suggestion) => {
              addSuggestion(suggestion);
              setShowAddForm(false);
            }}
            onCancel={() => setShowAddForm(false)}
          />
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            style={{ width: '100%', padding: '6px', fontSize: '11px', fontWeight: 700, color: '#374151', background: 'white', border: '1px dashed #D1D5DB', borderRadius: '5px', cursor: 'pointer' }}
          >+ Add New Code</button>
        )}
      </div>
      */}

      {/* ── Gap Type View ─────────────────────────────────────────── */}
      {viewMode === 'gapType' && (
      <div style={{ padding: '8px 0' }}>
        {pending.length === 0 ? (
          <div style={{ padding: '20px 12px', textAlign: 'center', color: '#9CA3AF', fontSize: '11px' }}>
            All suggestions reviewed ✓
          </div>
        ) : (
          pending.map((s) => {
            const isLoading = actionLoading[s.id] ?? false;
            const gapColor = s.gapType === 'suspect_new' ? '#1D4ED8' : s.gapType === 'opportunity' ? '#15803D' : s.gapType === 'over_claimed' ? '#B91C1C' : s.gapType === 'recapture_stale' ? '#B45309' : '#6D28D9';
            const gapLabel = s.gapType === 'suspect_new' ? 'Suspecting Code Gap' : s.gapType === 'opportunity' ? 'Recaptured Opportunity' : s.gapType === 'over_claimed' ? 'Recaptured Risk' : s.gapType === 'recapture_stale' ? 'Suspecting Emerging' : 'Recaptured Existing';
            return (
              <div key={s.id} style={{
                borderLeft: `3px solid ${gapColor}`,
                padding: '8px 12px 8px 10px',
                borderBottom: '1px solid #F3F4F6',
                background: isLoading ? '#F9FAFB' : 'white',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Row 1: badge */}
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: gapColor, background: `${gapColor}15`, padding: '1px 5px', borderRadius: '8px' }}>{gapLabel}</span>
                    </div>
                    {/* Row 2: ICD code + description */}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap', marginBottom: '3px' }}>
                      <span style={{ fontSize: '15px', fontWeight: 700, fontFamily: 'monospace', color: '#111827', letterSpacing: '0.02em', flexShrink: 0 }}>{s.icdCode}</span>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#1F2937', lineHeight: 1.3 }}>{s.icdDescription}</span>
                    </div>
                    {s.justification && (
                      <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '2px', lineHeight: 1.4 }}>{s.justification}</div>
                    )}
                  </div>
                  {/* Quick actions */}
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <button
                      onClick={() => acceptSuggestion(s.id)}
                      disabled={isLoading}
                      style={{
                        background: accent,
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '10px',
                        fontWeight: 700,
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.6 : 1,
                      }}
                    >✓</button>
                    <button
                      onClick={() => setEditingId(s.id)}
                      disabled={isLoading}
                      title="Edit before accepting"
                      style={{
                        background: '#F3F4F6',
                        color: '#9CA3AF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '4px',
                        padding: '4px 6px',
                        fontSize: '10px',
                        fontWeight: 700,
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.6 : 1,
                      }}
                    >✎</button>
                    <button
                      onClick={() => rejectSuggestion(s.id, 'Rejected')}
                      disabled={isLoading}
                      style={{
                        background: '#F3F4F6',
                        color: '#6B7280',
                        border: '1px solid #E5E7EB',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '10px',
                        fontWeight: 700,
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.6 : 1,
                      }}
                    >✕</button>
                  </div>
                </div>
                {/* Expand toggle — pending gap-type */}
                {(s.clinicalReasoning || s.evidence?.length > 0) && (
                  <button
                    onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px', padding: '3px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: '10px', fontWeight: 600 }}
                  >
                    <span style={{ fontSize: '9px', transform: expandedId === s.id ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform 0.15s' }}>▶</span>
                    Justification{s.evidence?.length > 0 ? ` · ${s.evidence.length} source${s.evidence.length !== 1 ? 's' : ''}` : ''}
                  </button>
                )}
                {expandedId === s.id && <CompactEvidencePanel suggestion={s} accent={accent} />}
                {editingId === s.id && (
                  <CompactEditForm
                    suggestion={s}
                    accent={accent}
                    onSave={(updates) => { editSuggestion(s.id, updates); setEditingId(null); }}
                    onCancel={() => setEditingId(null)}
                  />
                )}
              </div>
            );
          })
        )}
      </div>
      )}

      {/* Reviewed suggestions (accepted / rejected) — gap type view */}
      {viewMode === 'gapType' && reviewed.length > 0 && (
        <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '4px' }}>
          {reviewed.map((s) => {
            const isAccepted = s.status === 'accepted';
            const reviewGapColor = s.gapType === 'suspect_new' ? '#1D4ED8' : s.gapType === 'opportunity' ? '#15803D' : s.gapType === 'over_claimed' ? '#B91C1C' : s.gapType === 'recapture_stale' ? '#B45309' : '#6D28D9';
            const reviewGapLabel = s.gapType === 'suspect_new' ? 'Suspecting Code Gap' : s.gapType === 'opportunity' ? 'Recaptured Opportunity' : s.gapType === 'over_claimed' ? 'Recaptured Risk' : s.gapType === 'recapture_stale' ? 'Suspecting Emerging' : 'Recaptured Existing';
            return (
              <div key={s.id} style={{ padding: '7px 12px 7px 10px', borderBottom: '1px solid #F3F4F6', background: '#FAFAFA', borderLeft: `3px solid ${isAccepted ? '#059669' : '#9CA3AF'}`, opacity: 0.75 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: reviewGapColor, background: `${reviewGapColor}15`, padding: '1px 4px', borderRadius: '6px' }}>{reviewGapLabel}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap', marginBottom: '2px' }}>
                      <span style={{ fontSize: '15px', fontWeight: 700, fontFamily: 'monospace', color: '#6B7280', letterSpacing: '0.02em', flexShrink: 0 }}>{s.icdCode}</span>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', lineHeight: 1.3 }}>{s.icdDescription}</span>
                    </div>
                    {s.justification && (
                      <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '2px', lineHeight: 1.4 }}>{s.justification}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                    <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '8px', background: isAccepted ? '#ECFDF5' : '#F3F4F6', color: isAccepted ? '#059669' : '#6B7280' }}>
                      {isAccepted ? '✓ Accepted' : '✕ Rejected'}
                    </span>
                    <button
                      onClick={() => resetSuggestionStatus(s.id)}
                      disabled={actionLoading[s.id] ?? false}
                      title="Undo"
                      style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: '4px', padding: '2px 5px', cursor: (actionLoading[s.id] ?? false) ? 'not-allowed' : 'pointer', fontSize: '11px', color: '#9CA3AF', lineHeight: 1, opacity: (actionLoading[s.id] ?? false) ? 0.4 : 1 }}
                    >↩</button>
                  </div>
                </div>
                {(s.clinicalReasoning || s.evidence?.length > 0) && (
                  <button
                    onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', padding: '2px 0', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: '10px', fontWeight: 600 }}
                  >
                    <span style={{ fontSize: '9px', transform: expandedId === s.id ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform 0.15s' }}>▶</span>
                    Justification{s.evidence?.length > 0 ? ` · ${s.evidence.length} source${s.evidence.length !== 1 ? 's' : ''}` : ''}
                  </button>
                )}
                {expandedId === s.id && <CompactEvidencePanel suggestion={s} accent={accent} />}
              </div>
            );
          })}
        </div>
      )}

      {/* Open panel CTA */}
      <div style={{ padding: '10px 12px', borderTop: '1px solid #F3F4F6', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <button
          onClick={openFullPanel}
          style={{
            width: '100%',
            background: accent,
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '8px',
            fontSize: '11px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          Open Full Panel
          <span style={{ fontSize: '12px' }}>→</span>
        </button>
      </div>

      {/* Attestation warning popup */}
      {attestWarning && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'rgba(0,0,0,0.4)' }} onClick={() => setAttestWarning(null)}>
          <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 12px 32px rgba(0,0,0,0.2)', width: '100%', maxWidth: '260px', padding: '16px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: attestWarning.allowAttest ? '#DBEAFE' : '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '14px' }}>{attestWarning.allowAttest ? 'ℹ' : '⚠'}</div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}>{attestWarning.allowAttest ? 'Confirm Attestation' : 'Review Required'}</div>
                <div style={{ fontSize: '11px', color: '#6B7280', lineHeight: 1.4 }}>{attestWarning.message}</div>
              </div>
            </div>
            {attestWarning.allowAttest ? (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => setAttestWarning(null)} style={{ flex: 1, padding: '6px', fontSize: '11px', fontWeight: 700, color: '#374151', background: '#F3F4F6', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Go Back</button>
                <button onClick={() => { setAttestWarning(null); attestSession(launch.providerName ?? 'Provider'); }} style={{ flex: 1, padding: '6px', fontSize: '11px', fontWeight: 700, color: 'white', background: '#059669', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Attest</button>
              </div>
            ) : (
              <button onClick={() => setAttestWarning(null)} style={{ width: '100%', padding: '6px', fontSize: '11px', fontWeight: 700, color: '#374151', background: '#F3F4F6', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>OK</button>
            )}
          </div>
        </div>
      )}

      </div>{/* end scrollable content */}

      {/* Sticky attestation footer */}
      {attestation ? (
        <div style={{ flexShrink: 0, padding: '8px 12px', background: '#ECFDF5', borderTop: '2px solid #A7F3D0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#059669' }}>
              Attested{launch.providerSpecialty && launch.providerSpecialty !== 'PCP' ? ` (${launch.providerSpecialty})` : ''}
            </span>
          </div>
          <span style={{ fontSize: '10px', color: '#059669' }}>{attestation.attestedBy} · {new Date(attestation.attestedAt).toLocaleTimeString()}</span>
        </div>
      ) : (
        <div style={{ flexShrink: 0, padding: '8px 12px', background: 'white', borderTop: '2px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
              <span style={{ fontSize: '10px', fontWeight: 600, color: '#6B7280' }}>{filteredSuggestions.filter(s => s.status !== 'pending').length} of {filteredTotal} reviewed</span>
              <span style={{ fontSize: '10px', color: '#9CA3AF' }}>{pct}%</span>
            </div>
            <div style={{ height: '4px', background: '#F3F4F6', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: allReviewed ? '#059669' : '#60A5FA', borderRadius: '4px', transition: 'width 0.4s ease' }} />
            </div>
          </div>
          <button
            onClick={handleAttest}
            disabled={filteredTotal === 0}
            style={{
              flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '6px 12px',
              fontSize: '11px', fontWeight: 700,
              border: 'none', borderRadius: '6px', cursor: filteredTotal === 0 ? 'not-allowed' : 'pointer',
              background: allReviewed ? '#059669' : '#F3F4F6',
              color: allReviewed ? 'white' : '#6B7280',
              transition: 'all 0.2s',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Attest & Sign
          </button>
        </div>
      )}

    </div>
  );
}

// ─── Evidence panel (compact inline version) ─────────────────────────────────

const DOC_ICON: Record<string, string> = {
  lab_result: '🧪', progress_note: '📝', consult: '👨‍⚕️', medication_list: '💊',
  procedure_report: '🔬', vital_signs: '📊', imaging: '🩻', discharge_summary: '🏥', other: '📄',
};
const DOC_LABEL: Record<string, string> = {
  lab_result: 'Lab Result', progress_note: 'Progress Note', consult: 'Consult Note',
  medication_list: 'Medication List', procedure_report: 'Procedure Report',
  vital_signs: 'Vital Signs', imaging: 'Imaging', discharge_summary: 'Discharge Summary', other: 'Document',
};

function CompactEvidencePanel({ suggestion, accent }: { suggestion: CodingSuggestion; accent: string }) {
  return (
    <div style={{ marginTop: '8px', borderTop: '1px solid #E5E7EB', paddingTop: '8px' }}>
      {/* Justification */}
      {suggestion.clinicalReasoning && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
          <span style={{ fontSize: '13px', flexShrink: 0, marginTop: '1px' }}>🧠</span>
          <div>
            <div style={{ fontSize: '9px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '3px' }}>Justification</div>
            <div style={{ fontSize: '10px', color: '#374151', lineHeight: 1.5 }}>{suggestion.clinicalReasoning}</div>
          </div>
        </div>
      )}
      {/* Evidence cards */}
      {suggestion.evidence?.length > 0 && (
        <div>
          <div style={{ fontSize: '9px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '6px' }}>
            Supporting Evidence ({suggestion.evidence.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {suggestion.evidence.map((ev, i) => {
              const pct = Math.round((ev.relevanceScore ?? 0.85) * 100);
              const barColor = pct >= 90 ? '#10B981' : pct >= 75 ? accent : '#F59E0B';
              return (
                <div key={i} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                  {/* Source header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', flexShrink: 0 }}>{DOC_ICON[ev.documentType] ?? '📄'}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.sourceDocument}</div>
                      <div style={{ fontSize: '9px', color: '#9CA3AF' }}>
                        {DOC_LABEL[ev.documentType] ?? 'Document'}
                        {ev.documentDate ? ` · ${new Date(ev.documentDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                      </div>
                    </div>
                  </div>
                  {/* Relevance bar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '9px', color: '#9CA3AF', width: '50px', flexShrink: 0 }}>Relevance</span>
                    <div style={{ flex: 1, height: '4px', background: '#E5E7EB', borderRadius: '99px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: '99px' }} />
                    </div>
                    <span style={{ fontSize: '9px', color: '#6B7280', width: '28px', textAlign: 'right' }}>{pct}%</span>
                  </div>
                  {/* Key findings */}
                  {ev.keyFindings?.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '6px' }}>
                      {ev.keyFindings.map((f, j) => (
                        <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                          <span style={{ color: '#10B981', fontSize: '10px', flexShrink: 0, marginTop: '1px' }}>✓</span>
                          <span style={{ fontSize: '10px', color: '#374151', lineHeight: 1.4 }}>{f}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Excerpt */}
                  {ev.excerpt && (
                    <div>
                      <div style={{ fontSize: '9px', color: '#9CA3AF', marginBottom: '3px' }}>Excerpt:</div>
                      <div style={{ fontSize: '10px', color: '#374151', lineHeight: 1.5, fontFamily: 'monospace', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '4px', padding: '5px 7px' }}>{ev.excerpt}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function CompactAddForm({ accent, onAdd, onCancel }: { accent: string; onAdd: (suggestion: Omit<CodingSuggestion, 'id'>) => void; onCancel: () => void }) {
  const [icdCode, setIcdCode] = useState('');
  const [icdDesc, setIcdDesc] = useState('');
  const [gapType, setGapType] = useState<GapType>('suspect_new');
  const [hccCategory, setHccCategory] = useState('');
  const [hccDescription, setHccDescription] = useState('');
  const [hccWeight] = useState(0);
  const [justification, setJustification] = useState('');
  const [clinicalReasoning, setClinicalReasoning] = useState('');

  interface EvForm { sourceDocument: string; documentType: DocumentType; excerpt: string; keyFindings: string }
  const [evidenceList, setEvidenceList] = useState<EvForm[]>([{ sourceDocument: '', documentType: 'progress_note' as DocumentType, excerpt: '', keyFindings: '' }]);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ classification: false, hcc: false, reasoning: false, evidence: false });
  const toggleSection = (key: string) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const canSave = icdCode.trim() && icdDesc.trim();

  const handleAdd = () => {
    if (!canSave) return;
    onAdd({
      icdCode: icdCode.trim(),
      icdDescription: icdDesc.trim(),
      gapType,
      status: 'pending',
      hccCategory: hccCategory.trim() || null,
      hccDescription: hccDescription.trim() || null,
      hccWeight,
      lastCapturedDate: null,
      justification: justification.trim(),
      clinicalReasoning: clinicalReasoning.trim() || 'Manually added by clinician.',
      evidence: evidenceList
        .filter(e => e.sourceDocument.trim() || e.excerpt.trim())
        .map(e => ({
          id: generateUUID(),
          sourceDocument: e.sourceDocument,
          documentType: e.documentType,
          documentDate: new Date().toISOString().split('T')[0],
          excerpt: e.excerpt,
          highlights: [],
          relevanceScore: 0.85,
          keyFindings: e.keyFindings.split('\n').map(s => s.trim()).filter(Boolean),
        })),
    });
  };

  const addEvidence = () => setEvidenceList(prev => [...prev, { sourceDocument: '', documentType: 'progress_note' as DocumentType, excerpt: '', keyFindings: '' }]);
  const removeEvidence = (idx: number) => setEvidenceList(prev => prev.filter((_, i) => i !== idx));
  const updateEvidence = (idx: number, field: keyof EvForm, value: string) => setEvidenceList(prev => prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));

  const inputStyle: React.CSSProperties = { width: '100%', padding: '4px 6px', fontSize: '12px', border: '1px solid #E5E7EB', borderRadius: '4px', outline: 'none', background: 'white' };
  const selectStyle: React.CSSProperties = { ...inputStyle, appearance: 'auto' as const };
  const textareaStyle: React.CSSProperties = { ...inputStyle, resize: 'none' as const };
  const sectionHeaderStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', cursor: 'pointer', fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', borderTop: '1px solid #E5E7EB', marginTop: '4px' };

  return (
    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '8px', maxHeight: '400px', overflowY: 'auto' }}>
      <div style={{ fontSize: '10px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>Add New Code</div>

      {/* Basic — always visible */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '4px' }}>
        <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600 }}>ICD Code *</div>
        <input value={icdCode} onChange={e => setIcdCode(e.target.value)} placeholder="ICD-10 code (e.g. E11.65)" style={{ ...inputStyle, fontFamily: 'monospace' }} />
        <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600, marginTop: '2px' }}>Description *</div>
        <input value={icdDesc} onChange={e => setIcdDesc(e.target.value)} placeholder="Type 2 diabetes mellitus..." style={inputStyle} />
      </div>

      {/* Classification — collapsible */}
      <div style={sectionHeaderStyle} onClick={() => toggleSection('classification')}>
        <span>Classification</span>
        <span style={{ fontSize: '10px' }}>{openSections.classification ? '▲' : '▼'}</span>
      </div>
      {openSections.classification && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', paddingBottom: '4px' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, marginBottom: '1px' }}>Gap Type</div>
              <select value={gapType} onChange={e => setGapType(e.target.value as GapType)} style={selectStyle}>
                {GAP_TYPES_COMPACT.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* HCC — collapsible */}
      <div style={sectionHeaderStyle} onClick={() => toggleSection('hcc')}>
        <span>HCC Details</span>
        <span style={{ fontSize: '10px' }}>{openSections.hcc ? '▲' : '▼'}</span>
      </div>
      {openSections.hcc && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', paddingBottom: '4px' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, marginBottom: '1px' }}>Category</div>
            <input value={hccCategory} onChange={e => setHccCategory(e.target.value)} placeholder="HCC 19" style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, marginBottom: '1px' }}>HCC Description</div>
            <input value={hccDescription} onChange={e => setHccDescription(e.target.value)} placeholder="Diabetes without complication" style={inputStyle} />
          </div>
        </div>
      )}

      {/* Reasoning — collapsible */}
      <div style={sectionHeaderStyle} onClick={() => toggleSection('reasoning')}>
        <span>Reasoning</span>
        <span style={{ fontSize: '10px' }}>{openSections.reasoning ? '▲' : '▼'}</span>
      </div>
      {openSections.reasoning && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', paddingBottom: '4px' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, marginBottom: '1px' }}>Justification</div>
            <textarea value={justification} onChange={e => setJustification(e.target.value)} rows={2} placeholder="Concise reason for this code..." style={textareaStyle} />
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, marginBottom: '1px' }}>Clinical Reasoning</div>
            <textarea value={clinicalReasoning} onChange={e => setClinicalReasoning(e.target.value)} rows={2} placeholder="Clinical rationale..." style={textareaStyle} />
          </div>
        </div>
      )}

      {/* Evidence — collapsible */}
      <div style={sectionHeaderStyle} onClick={() => toggleSection('evidence')}>
        <span>Evidence ({evidenceList.filter(e => e.sourceDocument.trim()).length})</span>
        <span style={{ fontSize: '10px' }}>{openSections.evidence ? '▲' : '▼'}</span>
      </div>
      {openSections.evidence && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingBottom: '4px' }}>
          {evidenceList.map((ev, idx) => (
            <div key={idx} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '4px', padding: '6px', position: 'relative' }}>
              {evidenceList.length > 1 && (
                <button onClick={() => removeEvidence(idx)} style={{ position: 'absolute', top: '2px', right: '4px', background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: '12px', lineHeight: 1 }}>&times;</button>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <input value={ev.sourceDocument} onChange={e => updateEvidence(idx, 'sourceDocument', e.target.value)} placeholder="Source document" style={{ ...inputStyle, border: '1px solid #F3F4F6' }} />
                <select value={ev.documentType} onChange={e => updateEvidence(idx, 'documentType', e.target.value)} style={{ ...selectStyle, border: '1px solid #F3F4F6' }}>
                  {DOC_TYPES_COMPACT.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
                <textarea value={ev.excerpt} onChange={e => updateEvidence(idx, 'excerpt', e.target.value)} rows={2} placeholder="Clinical excerpt..." style={{ ...textareaStyle, border: '1px solid #F3F4F6' }} />
                <textarea value={ev.keyFindings} onChange={e => updateEvidence(idx, 'keyFindings', e.target.value)} rows={2} placeholder="Key findings (one per line)" style={{ ...textareaStyle, border: '1px solid #F3F4F6' }} />
              </div>
            </div>
          ))}
          <button onClick={addEvidence} style={{ width: '100%', padding: '4px', fontSize: '11px', fontWeight: 700, color: '#374151', background: 'white', border: '1px dashed #D1D5DB', borderRadius: '4px', cursor: 'pointer' }}>+ Add Evidence</button>
        </div>
      )}

      {/* Save / Cancel */}
      <div style={{ display: 'flex', gap: '4px', marginTop: '6px', borderTop: '1px solid #E5E7EB', paddingTop: '6px' }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '4px', fontSize: '10px', fontWeight: 600, border: '1px solid #E5E7EB', borderRadius: '4px', background: 'white', color: '#6B7280', cursor: 'pointer' }}>Cancel</button>
        <button onClick={handleAdd} disabled={!canSave} style={{ flex: 1, padding: '4px', fontSize: '10px', fontWeight: 700, border: 'none', borderRadius: '4px', background: canSave ? accent : '#D1D5DB', color: 'white', cursor: canSave ? 'pointer' : 'not-allowed' }}>Add</button>
      </div>
    </div>
  );
}

function CompactEditForm({ suggestion, accent, onSave, onCancel }: { suggestion: CodingSuggestion; accent: string; onSave: (updates: Partial<CodingSuggestion>) => void; onCancel: () => void }) {
  const [icdCode, setIcdCode] = useState(suggestion.icdCode);
  const [icdDesc, setIcdDesc] = useState(suggestion.icdDescription);
  const [gapType, setGapType] = useState<GapType>(suggestion.gapType);
  const [status, setStatus] = useState<SuggestionStatus>(suggestion.status);
  const [hccCategory, setHccCategory] = useState(suggestion.hccCategory ?? '');
  const [hccDescription, setHccDescription] = useState(suggestion.hccDescription ?? '');
  const [hccWeight] = useState(suggestion.hccWeight);
  const [justification, setJustification] = useState(suggestion.justification ?? '');
  const [clinicalReasoning, setClinicalReasoning] = useState(suggestion.clinicalReasoning ?? '');

  interface EvForm { sourceDocument: string; documentType: DocumentType; excerpt: string; keyFindings: string }
  const [evidenceList, setEvidenceList] = useState<EvForm[]>(
    suggestion.evidence?.length
      ? suggestion.evidence.map(ev => ({ sourceDocument: ev.sourceDocument, documentType: ev.documentType, excerpt: ev.excerpt, keyFindings: ev.keyFindings.join('\n') }))
      : [{ sourceDocument: '', documentType: 'progress_note' as DocumentType, excerpt: '', keyFindings: '' }],
  );

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ classification: false, hcc: false, reasoning: false, evidence: false });
  const toggleSection = (key: string) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const canSave = icdCode.trim() && icdDesc.trim();

  const handleSave = () => {
    if (!canSave) return;
    const updates: Partial<CodingSuggestion> = {
      icdCode: icdCode.trim(),
      icdDescription: icdDesc.trim(),
      gapType,
      status,
      hccCategory: hccCategory.trim() || null,
      hccDescription: hccDescription.trim() || null,
      hccWeight,
      justification: justification.trim(),
      clinicalReasoning: clinicalReasoning.trim(),
      evidence: evidenceList
        .filter(e => e.sourceDocument.trim() || e.excerpt.trim())
        .map(e => ({
          id: generateUUID(),
          sourceDocument: e.sourceDocument,
          documentType: e.documentType,
          documentDate: new Date().toISOString().split('T')[0],
          excerpt: e.excerpt,
          highlights: [],
          relevanceScore: 0.85,
          keyFindings: e.keyFindings.split('\n').map(s => s.trim()).filter(Boolean),
        })),
    };
    if (status === 'accepted') {
      updates.acceptedAt = suggestion.acceptedAt ?? new Date().toISOString();
      updates.acceptedBy = suggestion.acceptedBy ?? 'Provider';
    } else if (status === 'rejected') {
      updates.rejectedAt = suggestion.rejectedAt ?? new Date().toISOString();
      updates.rejectedBy = suggestion.rejectedBy ?? 'Provider';
      updates.rejectionReason = suggestion.rejectionReason ?? 'Edited';
    }
    onSave(updates);
  };

  const addEvidence = () => setEvidenceList(prev => [...prev, { sourceDocument: '', documentType: 'progress_note' as DocumentType, excerpt: '', keyFindings: '' }]);
  const removeEvidence = (idx: number) => setEvidenceList(prev => prev.filter((_, i) => i !== idx));
  const updateEvidence = (idx: number, field: keyof EvForm, value: string) => setEvidenceList(prev => prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));

  const inputStyle: React.CSSProperties = { width: '100%', padding: '4px 6px', fontSize: '12px', border: '1px solid #E5E7EB', borderRadius: '4px', outline: 'none', background: 'white' };
  const selectStyle: React.CSSProperties = { ...inputStyle, appearance: 'auto' as const };
  const textareaStyle: React.CSSProperties = { ...inputStyle, resize: 'none' as const };
  const sectionHeaderStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', cursor: 'pointer', fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', borderTop: '1px solid #DBEAFE', marginTop: '4px' };

  return (
    <div style={{ marginTop: '6px', background: '#F0F7FF', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '8px', maxHeight: '400px', overflowY: 'auto' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: '#1D4ED8', marginBottom: '6px' }}>Edit Suggestion</div>

      {/* Basic — always visible */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '4px' }}>
        <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600 }}>ICD Code *</div>
        <input value={icdCode} onChange={e => setIcdCode(e.target.value)} placeholder="E11.65" style={{ ...inputStyle, fontFamily: 'monospace' }} />
        <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600, marginTop: '2px' }}>Description *</div>
        <input value={icdDesc} onChange={e => setIcdDesc(e.target.value)} placeholder="Type 2 diabetes mellitus..." style={inputStyle} />
      </div>

      {/* Classification — collapsible */}
      <div style={sectionHeaderStyle} onClick={() => toggleSection('classification')}>
        <span>Classification</span>
        <span style={{ fontSize: '10px' }}>{openSections.classification ? '▲' : '▼'}</span>
      </div>
      {openSections.classification && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', paddingBottom: '4px' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, marginBottom: '1px' }}>Gap Type</div>
              <select value={gapType} onChange={e => setGapType(e.target.value as GapType)} style={selectStyle}>
                {GAP_TYPES_COMPACT.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, marginBottom: '1px' }}>Status</div>
              <select value={status} onChange={e => setStatus(e.target.value as SuggestionStatus)} style={selectStyle}>
                {STATUSES_COMPACT.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* HCC — collapsible */}
      <div style={sectionHeaderStyle} onClick={() => toggleSection('hcc')}>
        <span>HCC Details</span>
        <span style={{ fontSize: '10px' }}>{openSections.hcc ? '▲' : '▼'}</span>
      </div>
      {openSections.hcc && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', paddingBottom: '4px' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, marginBottom: '1px' }}>Category</div>
            <input value={hccCategory} onChange={e => setHccCategory(e.target.value)} placeholder="HCC 19" style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, marginBottom: '1px' }}>HCC Description</div>
            <input value={hccDescription} onChange={e => setHccDescription(e.target.value)} placeholder="Diabetes without complication" style={inputStyle} />
          </div>
        </div>
      )}

      {/* Reasoning — collapsible */}
      <div style={sectionHeaderStyle} onClick={() => toggleSection('reasoning')}>
        <span>Reasoning</span>
        <span style={{ fontSize: '10px' }}>{openSections.reasoning ? '▲' : '▼'}</span>
      </div>
      {openSections.reasoning && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', paddingBottom: '4px' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, marginBottom: '1px' }}>Justification</div>
            <textarea value={justification} onChange={e => setJustification(e.target.value)} rows={2} placeholder="Concise reason for this code..." style={textareaStyle} />
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, marginBottom: '1px' }}>Clinical Reasoning</div>
            <textarea value={clinicalReasoning} onChange={e => setClinicalReasoning(e.target.value)} rows={2} placeholder="Clinical rationale..." style={textareaStyle} />
          </div>
        </div>
      )}

      {/* Evidence — collapsible */}
      <div style={sectionHeaderStyle} onClick={() => toggleSection('evidence')}>
        <span>Evidence ({evidenceList.filter(e => e.sourceDocument.trim()).length})</span>
        <span style={{ fontSize: '10px' }}>{openSections.evidence ? '▲' : '▼'}</span>
      </div>
      {openSections.evidence && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingBottom: '4px' }}>
          {evidenceList.map((ev, idx) => (
            <div key={idx} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '4px', padding: '6px', position: 'relative' }}>
              {evidenceList.length > 1 && (
                <button onClick={() => removeEvidence(idx)} style={{ position: 'absolute', top: '2px', right: '4px', background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: '12px', lineHeight: 1 }}>&times;</button>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <input value={ev.sourceDocument} onChange={e => updateEvidence(idx, 'sourceDocument', e.target.value)} placeholder="Source document" style={{ ...inputStyle, border: '1px solid #F3F4F6' }} />
                <select value={ev.documentType} onChange={e => updateEvidence(idx, 'documentType', e.target.value)} style={{ ...selectStyle, border: '1px solid #F3F4F6' }}>
                  {DOC_TYPES_COMPACT.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
                <textarea value={ev.excerpt} onChange={e => updateEvidence(idx, 'excerpt', e.target.value)} rows={2} placeholder="Clinical excerpt..." style={{ ...textareaStyle, border: '1px solid #F3F4F6' }} />
                <textarea value={ev.keyFindings} onChange={e => updateEvidence(idx, 'keyFindings', e.target.value)} rows={2} placeholder="Key findings (one per line)" style={{ ...textareaStyle, border: '1px solid #F3F4F6' }} />
              </div>
            </div>
          ))}
          <button onClick={addEvidence} style={{ width: '100%', padding: '4px', fontSize: '11px', fontWeight: 700, color: '#1D4ED8', background: 'white', border: '1px dashed #BFDBFE', borderRadius: '4px', cursor: 'pointer' }}>+ Add Evidence</button>
        </div>
      )}

      {/* Save / Cancel */}
      <div style={{ display: 'flex', gap: '4px', marginTop: '6px', borderTop: '1px solid #BFDBFE', paddingTop: '6px' }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '4px', fontSize: '11px', fontWeight: 600, border: '1px solid #E5E7EB', borderRadius: '4px', background: 'white', color: '#6B7280', cursor: 'pointer' }}>Cancel</button>
        <button onClick={handleSave} disabled={!canSave} style={{ flex: 1, padding: '4px', fontSize: '11px', fontWeight: 700, border: 'none', borderRadius: '4px', background: canSave ? accent : '#D1D5DB', color: 'white', cursor: canSave ? 'pointer' : 'not-allowed' }}>Save Changes</button>
      </div>
    </div>
  );
}

function CompactSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid #E5E7EB', borderRadius: '3px', background: 'white', color: '#374151', outline: 'none', cursor: 'pointer', minWidth: '80px', maxWidth: '160px' }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Chip({ count, label, bg, color, border }: { count: number; label: string; bg: string; color: string; border: string }) {
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, color, borderRadius: '12px', padding: '2px 8px', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
      <span style={{ fontSize: '12px', fontWeight: 800 }}>{count}</span>
      {label}
    </div>
  );
}
